import React, { useState } from "react";
import { SelectField } from "@codway-dev/ui";
import { useEditorStore, EditorField } from "../../store";
import { OrderSummary } from "./preview/OrderSummary";
import { ShippingMethod } from "./preview/ShippingMethod";
import { TextInputPreview } from "./preview/inputs/TextInputPreview";
import { SelectInputPreview } from "./preview/inputs/SelectInputPreview";
import { CheckboxInputPreview } from "./preview/inputs/CheckboxInputPreview";
import { LocationSelectPreview } from "./preview/inputs/LocationSelectPreview";
import { UPSELL_EMBED_PREVIEW_REGISTRY } from "./UpsellEmbedRegistry";

const PICKUP_PREVIEW_VALUE = "";
const PICKUP_PREVIEW_LABEL = "Vista previa: Selector de Oficinas";
const PICKUP_PREVIEW_OPTIONS = [
  { label: "Vista previa: Selector de Oficinas", value: "" },
];

interface FieldPreviewProps {
  field: EditorField;
  error?: string;
  style?: React.CSSProperties;
}

export const FieldPreview: React.FC<FieldPreviewProps> = ({
  field,
  error,
  style,
}) => {
  const updateField = useEditorStore((state) => state.updateField);
  const editorMode = useEditorStore((state) => state.ui.editorMode);
  const [previewValue, setPreviewValue] = useState<string | undefined>(
    undefined
  );
  const previewDeliveryMode = useEditorStore(
    (state) => state.ui.previewDeliveryMode
  );
  const officesEnabled = useEditorStore(
    (state) => state.config.settings.offices?.enabled === true
  );
  const countryCode = useEditorStore((state) => state.config.country_code);
  const designSettings = useEditorStore(
    (state) => state.config.design.settings
  );
  const isPickupPreview = officesEnabled && previewDeliveryMode === "pickup";

  const renderInput = () => {
    // Embedded upsell offers render via their own registry. They carry
    // their own colors/borders/typography from the upsell editor, so we
    // intentionally skip the form-level `--cod-*` CSS variable injection
    // — the offer is meant to look the same regardless of which form
    // template embeds it.
    if (field.type === "offer_embed") {
      const EmbedComponent = UPSELL_EMBED_PREVIEW_REGISTRY[field.display_type];
      return <EmbedComponent resolvedData={field.resolved_data} />;
    }

    const updateValue = (val: string) => {
      if (editorMode === "preview") {
        setPreviewValue(val);
        return;
      }
      updateField(field.tempId, { defaultValue: val });
    };

    const fieldForDisplay =
      editorMode === "preview"
        ? { ...field, defaultValue: previewValue ?? field.defaultValue ?? "" }
        : field;

    const dynamicStyle: React.CSSProperties &
      Record<string, string | number | undefined> = { ...style };

    const globalFontSize = designSettings.font_size ?? 16;
    const fieldTextColor = designSettings.field_text_color || "#303030";
    const labelColor =
      field.style?.color || designSettings.text_color || "#303030";

    // --cod-* CSS variables (shared field component convention)
    dynamicStyle["--cod-font-size"] = `${globalFontSize}px`;
    dynamicStyle["--cod-font-family"] =
      field.style?.fontFamily || designSettings.font_family || "inherit";
    dynamicStyle["--cod-text-color"] = labelColor;
    dynamicStyle["--cod-field-text-color"] = fieldTextColor;
    dynamicStyle["--cod-border-color"] =
      field.border_color ||
      field.style?.borderColor ||
      ((designSettings as Record<string, unknown>).border_color as string) ||
      "#919191";
    dynamicStyle["--cod-field-bg-color"] =
      field.field_background_color ||
      ((designSettings as Record<string, unknown>)
        .field_background_color as string) ||
      "#fff";
    dynamicStyle["--cod-border-radius"] = `${
      field.style?.cornerRadius ?? designSettings.corner_radius ?? 8
    }px`;
    dynamicStyle["--cod-primary-color"] =
      designSettings.primary_color || "#008060";
    dynamicStyle["--cod-error-text"] = "#dc2626";
    dynamicStyle["--cod-field-padding"] = "0.5rem 0.75rem";
    dynamicStyle["--cod-field-font-size"] = "14px";

    type SpecialComponentProps = { label?: string };
    const SPECIAL_COMPONENTS: Record<
      string,
      React.FC<SpecialComponentProps>
    > = {
      order_summary: OrderSummary,
      shipping_method: ShippingMethod,
    };

    type InputComponentProps = {
      field: EditorField;
      dynamicStyle: React.CSSProperties;
      onUpdate: (value: string) => void;
      error?: string;
    };

    const INPUT_COMPONENTS: Record<string, React.FC<InputComponentProps>> = {
      text: TextInputPreview,
      email: TextInputPreview,
      tel: TextInputPreview,
      number: TextInputPreview,
      date: TextInputPreview,
      textarea: TextInputPreview,
      select: SelectInputPreview,
      checkbox: CheckboxInputPreview,
    };

    if (SPECIAL_COMPONENTS[field.type]) {
      const SpecialComponent = SPECIAL_COMPONENTS[field.type];
      return (
        <div style={dynamicStyle as React.CSSProperties}>
          <SpecialComponent label={field.label} />
        </div>
      );
    }

    if (
      (countryCode === "CO" || !countryCode) &&
      (field.key === "zone_code" || field.key === "city")
    ) {
      return (
        <LocationSelectPreview
          field={fieldForDisplay}
          dynamicStyle={dynamicStyle}
          onUpdate={updateValue}
          error={error}
          type={field.key === "city" ? "city" : "province"}
        />
      );
    }

    if (isPickupPreview && field.key === "address_1") {
      return (
        <div style={dynamicStyle as React.CSSProperties}>
          <SelectField
            label={field.label}
            value={PICKUP_PREVIEW_VALUE}
            options={PICKUP_PREVIEW_OPTIONS}
            onChange={() => {}}
            placeholder={PICKUP_PREVIEW_LABEL}
            required={field.required}
            disabled
          />
        </div>
      );
    }

    if (INPUT_COMPONENTS[field.type]) {
      const InputComponent = INPUT_COMPONENTS[field.type];
      return (
        <InputComponent
          field={fieldForDisplay}
          dynamicStyle={dynamicStyle}
          onUpdate={updateValue}
          error={error}
        />
      );
    }

    return (
      <div className="w-full p-2 border border-red-300 bg-red-50 text-red-700 rounded text-sm">
        Unknown field type: {field.type}
      </div>
    );
  };

  const shouldDimInPickup =
    isPickupPreview && (field.key === "address_2" || field.key === "zip");

  return (
    <div
      className="w-full relative transition-colors outline-none"
      style={{
        display: "inline-block",
        verticalAlign: "top",
      }}
    >
      <div className={shouldDimInPickup ? "relative opacity-50" : undefined}>
        {renderInput()}
      </div>

      {shouldDimInPickup && (
        <div className="absolute right-2 top-2 z-20 pointer-events-none">
          <span className="inline-block rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium px-2 py-1 border border-amber-200">
            Oculto en modo recogida
          </span>
        </div>
      )}

      {editorMode === "edit" && <div className="absolute inset-0 z-10" />}
    </div>
  );
};
